import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconApps,
  IconExternalLink,
  IconSearch,
} from "@tabler/icons-react";
import { apiFetch, friendlyError } from "../../api/client.js";
import { useAuth } from "../../auth/AuthContext.js";
import { StatusPill } from "../../components/StatusPill.js";
import { formatRam } from "./vmUtils.js";
import { appStatusCopy, type AppInstance, type CatalogAppSummary } from "./appUtils.js";

const POLL_MS = 5000;

export function AppsDashboard() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [instances, setInstances] = useState<AppInstance[] | null>(null);
  const [catalog, setCatalog] = useState<CatalogAppSummary[] | null>(null);
  const [catalogFailed, setCatalogFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  async function loadInstances() {
    try {
      const data = await apiFetch<{ instances: AppInstance[] }>("/api/apps/instances");
      setInstances(data.instances);
      setError(null);
    } catch (err) {
      setError(friendlyError(err, "Could not load your apps."));
    } finally {
      setLoading(false);
    }
  }

  async function loadCatalog() {
    try {
      const data = await apiFetch<{ apps: CatalogAppSummary[] }>("/api/apps/catalog");
      setCatalog(data.apps);
      setCatalogFailed(false);
    } catch {
      // Catalog stays on its last known data; the deploy grid simply
      // keeps whatever it had. A hard failure surfaces via the section's
      // empty text.
      setCatalog((prev) => prev ?? []);
      setCatalogFailed(true);
    }
  }

  useEffect(() => {
    void loadInstances();
    void loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void loadInstances();
    }, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const instanceCount = instances?.length ?? 0;
  const q = search.trim().toLowerCase();
  const filteredCatalog = (catalog ?? []).filter(
    (a) =>
      q === "" ||
      a.name.toLowerCase().includes(q) ||
      a.description.toLowerCase().includes(q) ||
      a.slug.toLowerCase().includes(q)
  );

  return (
    <Box>
      <Group justify="space-between" mb="lg" align="flex-end" wrap="nowrap">
        <Stack gap={4} miw={0}>
          <Title order={2}>Apps</Title>
          <Text size="sm" c="dimmed">
            {loading && instances === null
              ? "Loading your apps…"
              : instanceCount === 0
                ? "One-click apps, each with its own web address."
                : `${instanceCount} app${instanceCount === 1 ? "" : "s"}${
                    auth.session?.display_name ? ` for ${auth.session.display_name}` : ""
                  }`}
          </Text>
        </Stack>
        <Button
          variant="default"
          size="sm"
          onClick={() => {
            void loadInstances();
            void loadCatalog();
          }}
          loading={loading && instances === null}
        >
          Refresh
        </Button>
      </Group>

      {error && (
        <Alert color="red" variant="light" mb="md" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <SectionTitle title="My apps" />
      {loading && instances === null ? (
        <Stack gap="xs" align="center" py="xl">
          <Loader />
          <Text size="sm" c="dimmed">
            Loading your apps…
          </Text>
        </Stack>
      ) : instanceCount === 0 ? (
        <Text size="sm" c="dimmed" mb="md">
          You haven&apos;t deployed any apps yet — pick one from the catalog below.
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={700} mb="xl">
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Address</Table.Th>
                <Table.Th w={110}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {instances?.map((inst) => (
                <Table.Tr
                  key={inst.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/dashboard/apps/instances/${inst.id}`)}
                  className="vm-row"
                >
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <ThemeIcon
                        size="sm"
                        variant="light"
                        color="brand"
                        radius="md"
                        style={{ flex: "0 0 auto" }}
                      >
                        <IconApps size={15} stroke={1.75} />
                      </ThemeIcon>
                      <Stack gap={0}>
                        <Anchor
                          component="button"
                          type="button"
                          size="sm"
                          fw={600}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            navigate(`/dashboard/apps/instances/${inst.id}`);
                          }}
                          style={{ textAlign: "left" }}
                        >
                          {inst.name}
                        </Anchor>
                        <Text size="xs" c="dimmed">
                          {inst.template_slug}
                        </Text>
                      </Stack>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <StatusPill status={inst.status} label={appStatusCopy(inst.status)} />
                  </Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    {inst.url ? (
                      <Anchor
                        href={inst.url}
                        target="_blank"
                        rel="noreferrer"
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Group gap={4} wrap="nowrap">
                          {inst.url.replace(/^https?:\/\//, "")}
                          <IconExternalLink size={14} stroke={1.75} />
                        </Group>
                      </Anchor>
                    ) : (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="xs"
                      variant="default"
                      onClick={() => navigate(`/dashboard/apps/instances/${inst.id}`)}
                    >
                      Details
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <SectionTitle title="App catalog" />
      <TextInput
        placeholder="Search apps…"
        leftSection={<IconSearch size={16} stroke={1.75} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        mb="md"
        maw={420}
      />
      {catalog === null ? (
        <Group justify="center" py="md">
          <Loader />
        </Group>
      ) : filteredCatalog.length === 0 ? (
        <Text size="sm" c="dimmed">
          {search.trim()
            ? "No apps match your search."
            : catalogFailed
              ? "Couldn't load the app catalog right now."
              : "No apps are available yet — check back soon."}
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }}>
          {filteredCatalog.map((app) => (
            <AppCatalogCard key={app.slug} app={app} onDeploy={() => navigate(`/dashboard/apps/catalog/${app.slug}`)} />
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
}

function SectionTitle({ title }: { title: string }) {
  return (
    <Group gap="xs" mb="sm">
      <IconApps size={18} stroke={1.75} />
      <Title order={3}>{title}</Title>
    </Group>
  );
}

function AppCatalogCard({ app, onDeploy }: { app: CatalogAppSummary; onDeploy: () => void }) {
  const [iconFailed, setIconFailed] = useState(false);
  return (
    <Card padding="lg" withBorder style={{ display: "flex", flexDirection: "column" }}>
      <Stack gap="sm" style={{ flex: 1 }}>
        <Group gap="sm" wrap="nowrap" align="flex-start">
          {app.icon && !iconFailed ? (
            <Box
              style={{
                width: 34,
                height: 34,
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={app.icon}
                alt=""
                width={22}
                height={22}
                onError={() => setIconFailed(true)}
              />
            </Box>
          ) : (
            <ThemeIcon size="lg" variant="light" color="brand" radius="md" style={{ flex: "0 0 auto" }}>
              <IconApps size={18} stroke={1.75} />
            </ThemeIcon>
          )}
          <Stack gap={2} style={{ flex: 1 }}>
            <Text fw={600}>{app.name}</Text>
            {app.category && (
              <Badge size="sm" variant="light" color="gray" style={{ alignSelf: "flex-start" }}>
                {app.category}
              </Badge>
            )}
          </Stack>
        </Group>
        <Text size="sm" c="dimmed" style={{ flex: 1 }}>
          {app.description}
        </Text>
        <Group gap="xs" wrap="wrap">
          <Text size="xs" c="dimmed">
            ~{formatRam(app.resources.mem_mb)} RAM
          </Text>
          <Text size="xs" c="dimmed">
            · {app.variable_count === 0 ? "No setup needed" : `${app.variable_count} setting${app.variable_count === 1 ? "" : "s"} to configure`}
          </Text>
        </Group>
        <Button size="sm" variant="light" onClick={onDeploy}>
          Deploy
        </Button>
      </Stack>
    </Card>
  );
}
